import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import TabIndicator from '../common/TabIndicator';
import RichHtmlContent from '../common/RichHtmlContent';
import CommentSection from './CommentSection';
import SessionClipsTab from './SessionClipsTab';
import SessionMetadataPanel from './SessionMetadataPanel';
import SessionCaminoLinksPanel from './SessionCaminoLinksPanel';
import { isRichContentEmpty } from '../../utils/richText';

const BASE_TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'accion', label: 'Puntos Clave' },
  { id: 'comentarios', label: 'Comunidad' },
  { id: 'clips', label: 'Clips' },
];

const EDIT_TAB = { id: 'editar', label: 'Editar' };

export default function SessionDetails({
  session,
  videoRef,
  canEdit = false,
  isAdmin = false,
  onMetadataUpdated,
}) {
  const [activeTab, setActiveTab] = useState('resumen');
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const hasTranscript = !isRichContentEmpty(session.transcript);
  const tabs = canEdit ? [...BASE_TABS, EDIT_TAB] : BASE_TABS;
  const visibleTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : 'resumen';

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 transition-colors duration-300">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="px-3 py-1 gfi-gradient-bg text-brand-navy rounded-full text-[10px] font-black tracking-widest uppercase">
          {session.category}
        </span>
        <span className="text-[10px] font-black text-[var(--text-main)] uppercase tracking-widest flex items-center gap-1.5">
          <SafeIcon name="BarChart" className="w-4 h-4 text-brand-cyan" /> {session.level}
        </span>
      </div>

      <h1 className="text-3xl md:text-5xl font-black text-[var(--text-main)] mb-8 leading-[1.1] tracking-tighter uppercase italic">
        {session.title}
      </h1>
      
      <div className="flex items-center gap-4 mb-12 p-6 bg-[var(--bg-surface)] rounded-2xl border border-[var(--border-subtle)] shadow-sm">
        <div className="w-14 h-14 rounded-full gfi-gradient-bg p-[2px] shadow-lg">
          <div className="w-full h-full rounded-full bg-[var(--bg-surface)] flex items-center justify-center text-xl font-black text-[var(--text-main)]">
            {session.speaker.charAt(0)}
          </div>
        </div>
        <div>
          <p className="text-lg font-black text-[var(--text-main)] uppercase tracking-tight italic">{session.speaker}</p>
          <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-[0.2em]">Liderazgo GFI Latino</p>
        </div>
        <button className="ml-auto flex items-center gap-2 px-5 py-2.5 bg-[var(--text-main)] text-[var(--bg-base)] rounded-full text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all active:scale-95 border border-white/10">
          Seguir Mentor
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-6 md:gap-10 border-b border-[var(--border-subtle)] mb-10 relative">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`pb-5 text-[11px] font-black uppercase tracking-[0.15em] transition-all relative ${
              visibleTab === tab.id ? 'text-[var(--text-main)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]/70'
            }`}
          >
            {tab.label}
            {visibleTab === tab.id && <TabIndicator />}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="min-h-[400px]">
        {visibleTab === 'resumen' && (
          <div className="max-w-3xl">
            <RichHtmlContent
              content={session.summary}
              className="text-xl tracking-tight"
            />
            {hasTranscript && (
              <div className="mt-10 pt-8 border-t border-[var(--border-subtle)]">
                <button
                  type="button"
                  onClick={() => setTranscriptExpanded((prev) => !prev)}
                  aria-expanded={transcriptExpanded}
                  className="w-full flex items-center justify-between gap-3 group"
                >
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)]">
                    Transcripción
                  </h3>
                  <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-brand-cyan group-hover:text-brand-green flex-shrink-0 transition-colors">
                    {transcriptExpanded ? 'Ocultar' : 'Ver'}
                    <SafeIcon
                      name={transcriptExpanded ? 'ChevronUp' : 'ChevronDown'}
                      className="w-3.5 h-3.5"
                    />
                  </span>
                </button>
                {transcriptExpanded && (
                  <div className="mt-6">
                    <RichHtmlContent
                      content={session.transcript}
                      className="text-xl tracking-tight"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {visibleTab === 'accion' && (
          <div className="grid gap-4 max-w-2xl">
            <h3 className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] mb-2">Checklist de Implementación</h3>
            {session.actionPoints?.length > 0 ? (
              session.actionPoints.map((point, idx) => (
                <div key={idx} className="flex gap-4 p-5 bg-[var(--bg-surface)] rounded-2xl border border-[var(--border-subtle)] shadow-sm group hover:border-brand-green/30 transition-all">
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <SafeIcon name="Check" className="w-3.5 h-3.5 text-brand-green" />
                  </div>
                  <span className="text-[var(--text-main)] font-bold text-sm leading-snug">{point}</span>
                </div>
              ))
            ) : (
              <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                Aún no hay puntos de acción para esta sesión.
              </p>
            )}
          </div>
        )}

        {visibleTab === 'comentarios' && <CommentSection sessionId={session.id} />}

        {visibleTab === 'clips' && (
          <SessionClipsTab
            session={session}
            videoRef={videoRef}
            canEdit={canEdit}
          />
        )}

        {visibleTab === 'editar' && canEdit && (
          <div className="space-y-6 max-w-3xl">
            <SessionMetadataPanel
              session={session}
              canEdit={canEdit}
              onMetadataUpdated={onMetadataUpdated}
            />
            <SessionCaminoLinksPanel sessionId={session.id} isAdmin={isAdmin} />
          </div>
        )}
      </div>
    </div>
  );
}
